import React, { useState } from 'react';
import { BASE_OPTIONS, generateServiceUrl, SERVICE_TYPES, ServiceType } from './launcherConfig';

interface TooltipPosition {
  x: number;
  y: number;
  placement: 'left' | 'right' | 'top' | 'bottom';
}

interface ServiceTypesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const styles = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  
  @keyframes slideIn {
    from { 
      opacity: 0; 
      transform: scale(0.95) translateY(10px);
    }
    to { 
      opacity: 1; 
      transform: scale(1) translateY(0);
    }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.3s ease-out;
  }
  
  .animate-slideIn {
    animation: slideIn 0.2s ease-out;
  }
`;

if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

const CloseIcon: React.FC = () => (
  <svg className="h-6 w-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const InfoIcon: React.FC = () => (
  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

export const ServiceTypesModal: React.FC<ServiceTypesModalProps> = ({ isOpen, onClose }) => {
  const [hoveredService, setHoveredService] = useState<ServiceType | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({
    x: 0,
    y: 0,
    placement: 'right'
  });

  if (!isOpen) return null;

  const handleInfoHover = (service: ServiceType, event: React.MouseEvent<HTMLDivElement>): void => {
    const iconRect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 280;
    const tooltipHeight = 120;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 12;
    const spaceRight = viewportWidth - iconRect.right;
    const spaceLeft = iconRect.left;
    const spaceTop = iconRect.top;

    let x: number, y: number, placement: TooltipPosition['placement'];

    if (spaceRight >= tooltipWidth + margin) {
      placement = 'right';
      x = iconRect.right + margin;
      y = iconRect.top + (iconRect.height / 2) - (tooltipHeight / 2);
    } else if (spaceLeft >= tooltipWidth + margin) {
      placement = 'left';
      x = iconRect.left - tooltipWidth - margin;
      y = iconRect.top + (iconRect.height / 2) - (tooltipHeight / 2);
    } else if (spaceTop >= tooltipHeight + margin) {
      placement = 'top';
      x = iconRect.left + (iconRect.width / 2) - (tooltipWidth / 2);
      y = iconRect.top - tooltipHeight - margin;
    } else {
      placement = 'bottom';
      x = iconRect.left + (iconRect.width / 2) - (tooltipWidth / 2);
      y = iconRect.bottom + margin;
    }

    x = Math.max(8, Math.min(x, viewportWidth - tooltipWidth - 8));
    y = Math.max(8, Math.min(y, viewportHeight - tooltipHeight - 8));

    setTooltipPosition({ x, y, placement });
    setHoveredService(service);
  };

  const handleInfoLeave = (): void => {
    setHoveredService(null);
  };

  const handleServiceSelect = (service: ServiceType): void => {
    const url = generateServiceUrl(service);
    window.location.href = url;
    onClose();
  };

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>, fallbackLetter: string): void => {
    const target = event.target as HTMLImageElement;
    target.src = `data:image/svg+xml,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100">
        <rect width="100" height="100" fill="#f3f4f6"/>
        <text x="50" y="50" text-anchor="middle" dy=".3em" font-family="Arial, sans-serif" font-size="12" fill="#9ca3af">
          ${fallbackLetter}
        </text>
      </svg>
    `)}`;
  };

  const handleTooltipImageError = (event: React.SyntheticEvent<HTMLImageElement>, fallbackLetter: string): void => {
    const target = event.target as HTMLImageElement;
    target.src = `data:image/svg+xml,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
        <rect width="48" height="48" fill="#f3f4f6"/>
        <text x="24" y="24" text-anchor="middle" dy=".3em" font-family="Arial, sans-serif" font-size="18" fill="#9ca3af">
          ${fallbackLetter}
        </text>
      </svg>
    `)}`;
  };

  const getArrowStyles = (placement: TooltipPosition['placement']): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      borderStyle: 'solid',
    };

    switch (placement) {
      case 'right':
        return {
          ...baseStyle,
          borderWidth: '8px 8px 8px 0',
          borderColor: 'transparent white transparent transparent'
        };
      case 'left':
        return {
          ...baseStyle,
          borderWidth: '8px 0 8px 8px',
          borderColor: 'transparent transparent transparent white'
        };
      case 'top':
        return {
          ...baseStyle,
          borderWidth: '8px 8px 0 8px',
          borderColor: 'white transparent transparent transparent'
        };
      case 'bottom':
        return {
          ...baseStyle,
          borderWidth: '0 8px 8px 8px',
          borderColor: 'transparent transparent white transparent'
        };
      default:
        return baseStyle;
    }
  };

  const getArrowBorderStyles = (placement: TooltipPosition['placement']): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      borderStyle: 'solid',
    };

    switch (placement) {
      case 'right':
        return {
          ...baseStyle,
          borderWidth: '9px 9px 9px 0',
          borderColor: 'transparent #e5e7eb transparent transparent'
        };
      case 'left':
        return {
          ...baseStyle,
          borderWidth: '9px 0 9px 9px',
          borderColor: 'transparent transparent transparent #e5e7eb'
        };
      case 'top':
        return {
          ...baseStyle,
          borderWidth: '9px 9px 0 9px',
          borderColor: '#e5e7eb transparent transparent transparent'
        };
      case 'bottom':
        return {
          ...baseStyle,
          borderWidth: '0 9px 9px 9px',
          borderColor: 'transparent transparent #e5e7eb transparent'
        };
      default:
        return baseStyle;
    }
  };

  const getArrowPositionClasses = (placement: TooltipPosition['placement']): string => {
    switch (placement) {
      case 'right':
        return 'left-0 top-1/2 -translate-y-1/2 -translate-x-2';
      case 'left':
        return 'right-0 top-1/2 -translate-y-1/2 translate-x-2';
      case 'top':
        return 'bottom-0 left-1/2 -translate-x-1/2 translate-y-2';
      case 'bottom':
        return 'top-0 left-1/2 -translate-x-1/2 -translate-y-2';
      default:
        return '';
    }
  };

  const getArrowBorderPositionClasses = (placement: TooltipPosition['placement']): string => {
    switch (placement) {
      case 'right':
        return 'left-0 top-1/2 -translate-y-1/2 -translate-x-[9px]';
      case 'left':
        return 'right-0 top-1/2 -translate-y-1/2 translate-x-[9px]';
      case 'top':
        return 'bottom-0 left-1/2 -translate-x-1/2 translate-y-[9px]';
      case 'bottom':
        return 'top-0 left-1/2 -translate-x-1/2 -translate-y-[9px]';
      default:
        return '';
    }
  };

  const renderServiceCard = (service: ServiceType, isBaseOption: boolean = false) => {
    const baseClasses = "group relative bg-white border rounded-xl p-3 sm:p-4 cursor-pointer transition-all duration-300 transform hover:scale-[1.03] hover:-translate-y-1";
    const regularClasses = "border-gray-200 hover:shadow-xl hover:border-[#F36E29]";

    return (
      <div
        key={service.id}
        onClick={() => handleServiceSelect(service)}
        className={`${baseClasses} ${regularClasses}`}
      >
        {/* Image */}
        <div className="aspect-square bg-gray-100 rounded-lg mb-3 sm:mb-4 flex items-center justify-center overflow-hidden">
          <img
            src={service.image}
            alt={service.name}
            className="w-full h-[150px] object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => handleImageError(e, service.name.charAt(0))}
          />
        </div>

        {/* Info */}
        <div className="space-y-1 sm:space-y-2">
          <h3 className="font-semibold text-xs sm:text-sm leading-tight transition-colors text-gray-900 group-hover:text-[#F36E29]">
            {service.name}
          </h3>
        </div>
        {!isBaseOption &&
          <>
            <div
              className="absolute top-2 sm:top-3 right-2 sm:right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10"
              onMouseEnter={(e) => {
                e.stopPropagation();
                handleInfoHover(service, e);
              }}
              onMouseLeave={handleInfoLeave}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white rounded-full p-1.5 sm:p-2 shadow-lg border border-gray-300 hover:bg-gray-50">
                <InfoIcon />
              </div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-t opacity-0 group-hover:opacity-30 rounded-xl transition-all duration-300 pointer-events-none from-blue-50 to-transparent" />
          </>
        }
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden relative max-[810px]:mt-[5em]">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Select Service Type</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-200 rounded-full transition-colors duration-200"
            type="button"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="p-4 sm:p-6 md:p-8 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="mb-8">
            <div className="flex items-center space-x-2 mb-4">
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {BASE_OPTIONS.map((service) => renderServiceCard(service, true))}
            </div>
          </div>

          <div className="flex items-center mb-8">
            <div className="flex-1 border-t border-gray-300"></div>
            <div className="mx-4 text-sm text-gray-500 font-medium">OR CHOOSE A SPECIFIC PRODUCT</div>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>

          <div>
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
              {SERVICE_TYPES.map((service) => renderServiceCard(service, false))}
            </div>
          </div>
        </div>

        {(hoveredService && !hoveredService.isBaseOption) && (
          <div
            className="fixed bg-white rounded-lg shadow-2xl border border-gray-200 z-[60] animate-slideIn pointer-events-none"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              width: '280px',
              maxWidth: '90vw'
            }}
          >
            <div className="p-4">
              <div className="flex items-start space-x-3 mb-3">
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex-shrink-0 overflow-hidden">
                  <img
                    src={hoveredService.image}
                    alt={hoveredService.name}
                    className="w-full h-full object-cover"
                    onError={(e) => handleTooltipImageError(e, hoveredService.name.charAt(0))}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 text-sm leading-tight mb-1">
                    {hoveredService.name}
                  </h3>
                  <div className="flex items-center space-x-2 text-xs text-gray-500">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full font-medium bg-orange-100 text-orange-800">
                      {hoveredService.targetSite === 'DWS' ? 'DWS' : 'Emblem Wizard'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-sm text-[#808080] leading-relaxed">
                  {hoveredService.description}
                </p>
              </div>
            </div>
            <div
              className={`absolute w-0 h-0 ${getArrowPositionClasses(tooltipPosition.placement)}`}
              style={getArrowStyles(tooltipPosition.placement)}
            ></div>
            <div
              className={`absolute w-0 h-0 ${getArrowBorderPositionClasses(tooltipPosition.placement)}`}
              style={getArrowBorderStyles(tooltipPosition.placement)}
            ></div>
          </div>
        )}
      </div>
    </div>
  );
};